import { motion } from "motion/react";
import { panelClassName } from "@/lib/constants";
import type { PrayerLocation, PrayerSnapshot } from "@/types/prayer";

type FocusPanelProps = {
  focusedLocation: PrayerLocation | null;
  isFocused: boolean;
  nextLocation: PrayerLocation | null;
  onFocusNext: () => void;
  onFocusPrevious: () => void;
  onFocusSelected: () => void;
  onResetFocus: () => void;
  previousLocation: PrayerLocation | null;
  selectedLocation: PrayerLocation | null;
  snapshot: PrayerSnapshot | null;
};

const controlButtonClassName =
  "cursor-pointer rounded-2xl border border-zinc-800/60 bg-zinc-900/50 px-3 py-3 text-left transition-colors hover:border-zinc-700 hover:bg-zinc-800/50 disabled:cursor-not-allowed disabled:opacity-40";

const FocusPanel = ({
  focusedLocation,
  isFocused,
  nextLocation,
  onFocusNext,
  onFocusPrevious,
  onFocusSelected,
  onResetFocus,
  previousLocation,
  selectedLocation,
  snapshot,
}: FocusPanelProps) => {
  const activeTiming = snapshot?.activeEvent
    ? snapshot.timings.find((timing) => timing.event === snapshot.activeEvent)
    : null;
  const nextTiming = snapshot?.nextEvent
    ? snapshot.timings.find((timing) => timing.event === snapshot.nextEvent)
    : null;

  return (
    <motion.div
      animate={{ opacity: 1, x: 0 }}
      className={panelClassName}
      initial={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.4 }}
    >
      <div className="shrink-0 border-b border-zinc-800/50 px-5 pt-5 pb-4">
        <h2 className="text-lg font-semibold text-zinc-100">Focus Controls</h2>
        <p className="mt-1 text-xs text-zinc-500">
          Drive the earthMarker-style camera focus directly from the shell without leaving the globe scene.
        </p>
      </div>

      <div className="shrink-0 border-b border-zinc-800/50 px-5 py-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-4">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-400">Camera Status</p>
            <p className="mt-2 text-lg font-semibold text-zinc-100">{isFocused ? "Focused" : "Orbiting"}</p>
            <p className="mt-1 text-xs text-zinc-400">
              {isFocused
                ? "Auto-rotate is paused and the selected coordinate is pulled forward."
                : "Auto-rotate is active and the globe is in its wider exploratory state."}
            </p>
          </div>

          <div className="rounded-2xl border border-zinc-800/60 bg-zinc-900/60 p-4">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Current Target</p>
            <p className="mt-2 text-lg font-semibold text-zinc-100">{focusedLocation?.city ?? "Wide orbit view"}</p>
            <p className="mt-1 text-xs text-zinc-400">
              Selected location: {selectedLocation?.city ?? "None"}{" "}
              {selectedLocation ? `· ${selectedLocation.country}` : ""}
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <button
            className={controlButtonClassName}
            disabled={!selectedLocation}
            onClick={onFocusSelected}
            type="button"
          >
            <p className="text-sm font-semibold text-zinc-100">Focus Selected Location</p>
            <p className="mt-1 text-xs text-zinc-500">
              {selectedLocation ? `Jump to ${selectedLocation.city} and pause the orbit.` : "Select a location first."}
            </p>
          </button>

          <button className={controlButtonClassName} disabled={!isFocused} onClick={onResetFocus} type="button">
            <p className="text-sm font-semibold text-zinc-100">Reset Globe</p>
            <p className="mt-1 text-xs text-zinc-500">Return to the wider rotating view and clear the focus lock.</p>
          </button>

          <button className={controlButtonClassName} onClick={onFocusPrevious} type="button">
            <p className="text-sm font-semibold text-zinc-100">Previous Location</p>
            <p className="mt-1 text-xs text-zinc-500">
              {previousLocation ? `Shift focus to ${previousLocation.city}.` : "Cycle to the previous curated marker."}
            </p>
          </button>

          <button className={controlButtonClassName} onClick={onFocusNext} type="button">
            <p className="text-sm font-semibold text-zinc-100">Next Location</p>
            <p className="mt-1 text-xs text-zinc-500">
              {nextLocation ? `Shift focus to ${nextLocation.city}.` : "Cycle to the next curated marker."}
            </p>
          </button>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div className="rounded-2xl border border-zinc-800/60 bg-zinc-900/50 p-4">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Selected Coordinates</p>
            <p className="mt-2 font-mono text-sm text-zinc-200">
              {selectedLocation
                ? `${selectedLocation.lat.toFixed(4)}°, ${selectedLocation.lon.toFixed(4)}°`
                : "Select a marker"}
            </p>
            <p className="mt-1 text-xs text-zinc-500">
              {selectedLocation
                ? `${selectedLocation.city} · ${selectedLocation.timeZone}`
                : "The focus controls operate on the current selection."}
            </p>
          </div>

          <div className="rounded-2xl border border-zinc-800/60 bg-zinc-900/50 p-4">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Prayer Context</p>
            <p className="mt-2 text-sm font-semibold text-zinc-100">{activeTiming?.label ?? "Awaiting selection"}</p>
            <p className="mt-1 text-xs text-zinc-500">
              {nextTiming
                ? `Next: ${nextTiming.label} at ${nextTiming.time}.`
                : "Select a city to pair the camera focus with a live prayer state."}
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default FocusPanel;
